/**
 * Full-screen gate shown to members whose profile is not yet complete.
 * They cannot use the rest of the app until every required field + a
 * passport photo (<=1MB) is filled in.
 */
import { useEffect, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  MemberFields, MemberPhotoUpload, validateMemberForm, stripAadhar, formatAadhar,
} from "../components/MemberFormPieces";
import { SignOut, LockKey } from "@phosphor-icons/react";

const emptyEdit = {
  name: "", contact_no: "", alt_contact_no: "", father_name: "",
  date_of_joining: "", aadhar: "", full_address: "", designation: "",
};

export default function ProfileCompletionGate() {
  const { user, logout, refreshMe } = useAuth();
  const [form, setForm] = useState(emptyEdit);
  const [photo, setPhoto] = useState(user?.photo || null);
  const [saving, setSaving] = useState(false);

  // Pre-fill from currently-known user
  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        const { data } = await http.get(`/users/${user.id}`);
        setForm({
          name: data.name || "",
          contact_no: data.contact_no || "",
          alt_contact_no: data.alt_contact_no || "",
          father_name: data.father_name || "",
          date_of_joining: data.date_of_joining || "",
          aadhar: data.aadhar || "",
          full_address: data.full_address || "",
          designation: data.designation || "",
        });
        setPhoto(data.photo || null);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [user?.id]);

  const submit = async (e) => {
    e.preventDefault();
    const err = validateMemberForm({ ...form, password: "x", email: "x@x.com" }, { requirePassword: false });
    if (err) { toast.error(err); return; }
    if (!photo) { toast.error("Please upload your passport-size photo (≤1MB)"); return; }
    setSaving(true);
    try {
      const patch = {
        name: form.name,
        contact_no: form.contact_no,
        alt_contact_no: form.alt_contact_no,
        father_name: form.father_name,
        date_of_joining: form.date_of_joining,
        aadhar: stripAadhar(form.aadhar),
        full_address: form.full_address,
        designation: form.designation,
      };
      await http.patch(`/users/${user.id}`, patch);
      toast.success("Profile complete — welcome!");
      await refreshMe();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-100 p-4 sm:p-8" data-testid="profile-completion-gate">
      <div className="max-w-3xl mx-auto">
        <div className="bg-orange-600 text-white px-5 py-3 flex items-center gap-2 border-2 border-zinc-900">
          <LockKey size={16} weight="bold" />
          <span className="label-overline">Complete your profile to continue</span>
          <button
            type="button"
            onClick={logout}
            className="ml-auto label-overline flex items-center gap-1.5 border border-white/60 hover:bg-white hover:text-orange-700 px-2 py-1"
            data-testid="gate-logout"
          >
            <SignOut size={11} weight="bold" /> Sign out
          </button>
        </div>

        <div className="bg-white border-2 border-t-0 border-zinc-900 p-5 sm:p-8">
          <div className="label-overline text-blue-600 mb-1">Onboarding · Dream Home Planners</div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tighter uppercase text-zinc-900">
            Welcome, {user?.name}
          </h1>
          <p className="text-sm text-zinc-600 mt-2">
            Before you jump in, we need a few details on file. All fields below are required.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-6" data-testid="gate-form">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <MemberPhotoUpload
                userId={user.id}
                currentPhoto={photo}
                onUploaded={setPhoto}
                size={120}
              />
              <div className="flex-1 w-full">
                <MemberFields form={form} setForm={setForm} mode="self" />
              </div>
            </div>
            <div className="flex justify-end pt-4 border-t border-zinc-300">
              <Button
                type="submit" disabled={saving} data-testid="gate-submit"
                className="rounded-none h-11 bg-emerald-700 hover:bg-emerald-800 text-white font-mono uppercase tracking-widest text-xs px-6"
              >
                {saving ? "Saving…" : "Save & Continue"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
